import Image, { type ImageOptions } from "@tiptap/extension-image";
import {
  mergeAttributes,
  type Commands,
  type Editor,
  type RawCommands,
  type Command,
  type CommandProps,
  type Content,
  type Node as TiptapNode,
} from "@tiptap/core";
import type {
  NodeType,
  Fragment,
  Node as ProseMirrorNode,
  MarkType,
  ParseOptions,
} from "@tiptap/pm/model";
import { toProxyUrl } from "@/utils/imageUrl";

// Define a specific type for the options expected by our setImage command
interface SetResizableImageOptions {
  src: string;
  alt?: string;
  title?: string;
  width?: string | number | null; // Include width here if needed
  height?: string | number | null; // Include height here if needed
}

// Define types for the extended commands
// This is needed to satisfy the complex type structure Tiptap expects
declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    resizableImage: {
      /**
       * Add a resizable image
       */
      setImage: (options: SetResizableImageOptions) => ReturnType;
      /**
       * Set the width of a resizable image
       */
      setWidth: (width: string | number | null) => ReturnType;
      // setHeight: (height: string | number | null) => ReturnType;
    };
  }
}

const ResizableImage = Image.extend<ImageOptions>({
  name: "image",
  atom: true,

  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        renderHTML: (attributes) => {
          if (!attributes.width) {
            return {};
          }
          return {
            width: attributes.width,
          };
        },
        parseHTML: (element) => {
          const widthAttr = element.getAttribute("width");
          const styleWidth = element.style.width;
          if (widthAttr && widthAttr.match(/^\d+(px|%)$/)) {
            return widthAttr;
          }
          if (styleWidth && styleWidth.match(/^\d+(px|%)$/)) {
            return styleWidth;
          }
          return null;
        },
      },
      height: {
        default: null,
        renderHTML: (attributes) => {
          if (!attributes.height) {
            return {};
          }
          // Force height to auto if width is set, otherwise use attribute
          return { height: attributes.width ? "auto" : attributes.height };
        },
        parseHTML: (element) => {
          const heightAttr = element.getAttribute("height");
          const styleHeight = element.style.height;
          if (heightAttr && heightAttr.match(/^\d+(px|%)$/)) {
            return heightAttr;
          }
          if (styleHeight && styleHeight.match(/^\d+(px|%)$/)) {
            return styleHeight;
          }
          return null;
        },
      },
    };
  },

  addCommands() {
    return {
      setImage:
        (options: SetResizableImageOptions): Command =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: options,
          });
        },
      setWidth:
        (width: string | number | null): Command =>
        ({ commands }) => {
          // Ensure height is set to auto when width is changed
          const attrs = { width: width, height: width ? "auto" : null };
          return commands.updateAttributes(this.name, attrs);
        },
      // setHeight: (height: string | number | null): Command => ({ commands }) => {
      //   return commands.updateAttributes(this.name, { height: height });
      // },
    };
  },

  renderHTML({ node, HTMLAttributes }) {
    const { width, height } = node.attrs;
    const attrs = { ...HTMLAttributes };

    // Transform Azure blob URLs to proxy URLs for secure access
    if (attrs.src) {
      attrs.src = toProxyUrl(attrs.src) || attrs.src;
    }

    if (width) {
      attrs.width = width;
      // Ensure style is an object or initialize if needed
      attrs.style = typeof attrs.style === "string" ? attrs.style : "";
      attrs.style += ` width: ${width}${
        typeof width === "number" ? "px" : ""
      };`; // Explicitly add width to style
      attrs.style += ` height: auto;`; // Force height to auto when width is set
    } else if (height) {
      // Only apply height if width is not set
      attrs.height = height;
      attrs.style = typeof attrs.style === "string" ? attrs.style : "";
      attrs.style += ` height: ${height}${
        typeof height === "number" ? "px" : ""
      };`;
      attrs.style += ` width: auto;`; // Force width to auto if only height is set
    }

    // Clean up styles to remove potential duplicates or conflicts
    if (attrs.style) {
      attrs.style = attrs.style.trim();
      // Basic cleanup: remove duplicate properties, keeping the last one
      const styles = attrs.style
        .split(";")
        .map((s: string) => s.trim())
        .filter(Boolean);
      const styleMap = new Map<string, string>();
      styles.forEach((style: string) => {
        const [key, value] = style.split(":").map((s: string) => s.trim());
        if (key && value) {
          styleMap.set(key, value);
        }
      });
      attrs.style =
        Array.from(styleMap.entries())
          .map(([key, value]) => `${key}: ${value}`)
          .join("; ") + ";";
    }

    return ["img", mergeAttributes(this.options.HTMLAttributes, attrs)];
  },
});

export default ResizableImage;
